/**
 * The canonical data types a plot panel can chart.
 *
 * Two layers need this answer and reach it from different inputs: the panel
 * layer asks it of a hydrated `MessagePathNode` (`isViewableAsPlot`), and
 * panel-creation validation asks it of a raw `MessagePathRecord`. Structural
 * checks like leaf-ness genuinely have to be written twice, once per shape,
 * but the set of types does not — so it lives here and both import it.
 *
 * Keeping one copy matters most on the create path, which has no node-level
 * check behind it: `handleCreatePanel` never calls `panelAcceptsMessagePath`,
 * so for a panel built from records this set is the only thing standing
 * between a caller-named panel type and an unrenderable panel.
 */
import { isGeoSpecificType } from "./geoMessagePaths";
import { CanonicalDataType } from "./MessagePathRecord";

/**
 * Non-geographic types a plot accepts. `Array` is admitted whatever it holds;
 * `validateSignalForPanelType`'s Plot arm says why that is wider than the rule
 * choosing a default.
 */
const PLOTTABLE_CANONICAL_TYPES: CanonicalDataType[] = [
  CanonicalDataType.Number,
  CanonicalDataType.Boolean,
  CanonicalDataType.Timestamp,
  CanonicalDataType.Array,
  CanonicalDataType.NumberArray,
  CanonicalDataType.Categorical,
  CanonicalDataType.String,
];

/** Whether a plot panel can chart values of this canonical type. */
export function isPlottableCanonicalType(
  canonicalDataType: CanonicalDataType,
): boolean {
  return (
    PLOTTABLE_CANONICAL_TYPES.includes(canonicalDataType) ||
    isGeoSpecificType(canonicalDataType)
  );
}
